import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PenLine, Plus, Star, Trash2, UploadCloud, CheckCircle2, XCircle, ShieldCheck, Loader2,
} from "lucide-react";

const STYLE_LABELS = {
  INITIALS_ONLY: "Initials only",
  NAME_ONLY: "Name only",
  INITIALS_NAME: "Initials + Name",
  SIGNATURE_NAME: "Signature + Name",
  NAME_DESIGNATION: "Name + Designation",
  SIGNATURE_NAME_DESIGNATION: "Signature + Name + Designation",
};
const EMPTY = {
  full_name: "", initials: "", designation: "", department: "",
  organisation: "SUNTRONIX", email: "", mobile: "",
  signature_style: "SIGNATURE_NAME_DESIGNATION", active: true, is_default: false,
};

export default function AuthorisedSigners() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/one-sign/signers");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load signers");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.full_name || form.full_name.trim().length < 2) {
      toast.error("Full name is required"); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/one-sign/signers/${editing.id}`, form);
        toast.success("Signer updated");
      } else {
        await api.post("/one-sign/signers", form);
        toast.success("Signer created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const setDefault = async (s) => {
    try { await api.post(`/one-sign/signers/${s.id}/set-default`); toast.success(`${s.full_name} is now the default signer`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete signer "${s.full_name}"?`)) return;
    try { await api.delete(`/one-sign/signers/${s.id}`); toast.success("Signer deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const uploadSig = async (s, file) => {
    if (!file) return;
    setUploadingId(s.id);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/one-sign/signers/${s.id}/signature`, fd);
      toast.success("Signature mark uploaded (stored securely)"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="authorised-signers-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h1 className="text-xl font-semibold">Authorised Signers</h1>
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">ONE SIGN · Level 1</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Document authorisation identities used on issued quotations & proformas. Signature marks are
            uploaded &amp; approved (never AI-generated), stored securely and rendered server-side into
            documents — never exposed publicly.
          </p>
        </div>
        <Button variant="create" onClick={openNew} data-testid="add-signer-btn" className="bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1" /> Add signer
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-slate-400">No signers yet.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((s) => (
            <div key={s.id} data-testid={`signer-row-${s.id}`}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div className="h-12 w-12 shrink-0 rounded-lg bg-slate-100 grid place-items-center overflow-hidden border border-slate-200">
                {s.has_signature_image ? (
                  <img src={`${API}/one-sign/signers/${s.id}/signature-preview?v=${s.signature_version}`}
                    alt="mark" className="w-full h-full object-contain p-1"
                    data-testid={`signer-sig-${s.id}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="text-sm font-bold text-slate-500">{s.initials || "—"}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900 truncate">{s.full_name}</span>
                  {s.is_default && <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" />Default</Badge>}
                  {s.active
                    ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Active</Badge>
                    : <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-[10px]"><XCircle className="h-2.5 w-2.5 mr-0.5" />Inactive</Badge>}
                </div>
                <div className="text-[12px] text-slate-500 truncate">
                  {s.designation || "—"} · {s.organisation} · {STYLE_LABELS[s.signature_style] || s.signature_style}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <input type="file" accept="image/png,image/jpeg" hidden ref={fileRef} />
                <Button variant="ghost" size="sm" data-testid={`upload-sig-${s.id}`}
                  onClick={() => {
                    const inp = document.createElement("input");
                    inp.type = "file"; inp.accept = "image/png,image/jpeg";
                    inp.onchange = (e) => uploadSig(s, e.target.files?.[0]);
                    inp.click();
                  }}>
                  {uploadingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                </Button>
                {!s.is_default && (
                  <Button variant="ghost" size="sm" title="Set as default" data-testid={`default-sig-${s.id}`}
                    onClick={() => setDefault(s)}><Star className="h-4 w-4" /></Button>
                )}
                <Button variant="ghost" size="sm" title="Edit" data-testid={`edit-sig-${s.id}`}
                  onClick={() => openEdit(s)}><PenLine className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" title="Delete" data-testid={`delete-sig-${s.id}`}
                  onClick={() => remove(s)} disabled={s.is_default}
                  className="text-red-500 disabled:opacity-30"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="signer-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit signer" : "Add authorised signer"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Full name *</Label>
              <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
                data-testid="signer-name-input" placeholder="Pradeep Daga" />
            </div>
            <div>
              <Label className="text-xs">Initials</Label>
              <Input value={form.initials} onChange={(e) => set("initials", e.target.value)} placeholder="PD" />
            </div>
            <div>
              <Label className="text-xs">Designation</Label>
              <Input value={form.designation} onChange={(e) => set("designation", e.target.value)} placeholder="Managing Partner" />
            </div>
            <div>
              <Label className="text-xs">Department</Label>
              <Input value={form.department} onChange={(e) => set("department", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Organisation</Label>
              <Input value={form.organisation} onChange={(e) => set("organisation", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Signature style</Label>
              <Select value={form.signature_style} onValueChange={(v) => set("signature_style", v)}>
                <SelectTrigger data-testid="signer-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STYLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.is_default} onChange={(e) => set("is_default", e.target.checked)} /> Default signer
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-signer-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
